/**
 * 쪽 번호 (2026-08-21) — 무한 스크롤(“더 보기”) 대신.
 *
 * ★ 서버는 총 통수를 주지 않는다(`nextCursor` 뿐). 그래서 **지금까지 받아 온 것**을 쪽으로
 *   자르고, 더 받을 게 남아 있으면 `50+` 처럼 `+` 를 붙여 "적어도 이만큼" 이라고 말한다.
 *   마지막 쪽에서 다음을 누르면 그때 더 받아 온다 — 사용자에겐 쪽 넘김 하나로 보인다.
 * ★ 총 통수가 오게 되면(서버 `total`) 이 부품만 그 값을 쓰면 된다. 소비처는 그대로다.
 */

import { ChevronLeft, ChevronRight } from '../../icons';
import { tMail } from '../../lib/mail-i18n';

export const MAIL_PAGE_SIZES = [12, 25, 50, 100] as const;
export type MailPageSize = typeof MAIL_PAGE_SIZES[number];

interface MailPagerProps {
  page: number;
  pageSize: MailPageSize;
  /**
   * 총 통수 — 서버가 세 줬으면 그 값, 아니면 지금까지 받아 온 수.
   * 쪽 수를 세는 데 쓴다.
   */
  loaded: number;
  /**
   * ★ 이 쪽에 **실제로 그린** 통수. 총 통수와 따로 받는다 —
   *   서버가 214통이라 했는데 아직 3통만 받아 왔으면 `1–25` 가 아니라 `1–3` 이라고 적어야 한다.
   */
  shown: number;
  /** 더 받을 게 남았나. */
  hasMore: boolean;
  busy: boolean;
  onPage: (page: number) => void;
  onPageSize: (size: MailPageSize) => void;
}

/** 앞 3개 · 지금 주변 2개 · 마지막 1개. 사이는 `…`. */
function pageWindow(current: number, total: number): (number | '…')[] {
  const want = new Set<number>([1, 2, 3, current - 1, current, current + 1, total]);
  const keep = [...want].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const out: (number | '…')[] = [];
  let prev = 0;
  for (const n of keep) {
    if (prev && n - prev > 1) out.push('…');
    out.push(n);
    prev = n;
  }
  return out;
}

export function MailPager({ page, pageSize, loaded, shown, hasMore, busy, onPage, onPageSize }: MailPagerProps) {
  const pages = Math.max(1, Math.ceil(loaded / pageSize));
  const start = shown === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = shown === 0 ? 0 : start + shown - 1;
  // 마지막 쪽이어도 더 받을 게 남았으면 다음으로 갈 수 있다(눌렀을 때 받아 온다).
  const canNext = page < pages || hasMore;

  return (
    <div className="mail-pager">
      <span className="mail-pager-info">
        {start}–{end} / {loaded}{hasMore ? '+' : ''}
      </span>
      <span className="mail-pager-spacer" />
      <button
        type="button"
        className="mail-pager-btn"
        onClick={() => onPage(page - 1)}
        disabled={page <= 1 || busy}
        aria-label={tMail('mail.pager.prev')}
      >
        <ChevronLeft size={14} aria-hidden />
      </button>
      {pageWindow(page, pages).map((n, i) => (n === '…' ? (
        // eslint-disable-next-line react/no-array-index-key
        <span key={`gap-${i}`} className="mail-pager-gap">…</span>
      ) : (
        <button
          key={n}
          type="button"
          className="mail-pager-btn mail-pager-num"
          aria-current={n === page ? 'page' : undefined}
          onClick={() => onPage(n)}
          disabled={busy}
        >
          {n}
        </button>
      )))}
      {hasMore && <span className="mail-pager-gap" aria-hidden>…</span>}
      <button
        type="button"
        className="mail-pager-btn"
        onClick={() => onPage(page + 1)}
        disabled={!canNext || busy}
        aria-label={tMail('mail.pager.next')}
      >
        <ChevronRight size={14} aria-hidden />
      </button>
      <select
        className="mail-pager-size"
        value={pageSize}
        onChange={(e) => onPageSize(Number(e.target.value) as MailPageSize)}
        aria-label={tMail('mail.pager.size_aria')}
      >
        {MAIL_PAGE_SIZES.map((s) => (
          <option key={s} value={s}>{tMail('mail.pager.size_prefix').replace('{n}', String(s))}</option>
        ))}
      </select>
    </div>
  );
}
